<template>
  <VCol cols="12" class="account-settings-ai-processor">
    <SettingsCard
      :title="t('title')"
      :subtitle="t('subtitle')"
    >
      <template #tooltip>
        <div class="tooltip__content-text-wrapper">
          <div class="tooltip__content-text">
            <p>{{ t('tooltip.p1') }}</p>
            <p>{{ t('tooltip.p2') }}</p>
            <p>{{ t('tooltip.p3') }}</p>
            <p>{{ t('tooltip.p4') }}</p>
          </div>
        </div>
      </template>

          <Preloader v-if="isLoading" />
          <template v-else>
            <div class="account-settings__fields">
              <div
                v-for="key in filteredSettings"
                :key="key"
                class="account-settings__field-row"
                :class="{
                  'account-settings__field-row--switch': true,
                  'account-settings__field-row--no-border': isNoBorderField(key),
                }"
              >
                <template v-if="key === 'prompt'">
                  <div class="account-settings__field-row-header">
                    <div class="account-settings__field-label">
                      {{ t('promptTitle') }}
                    </div>
                    <button
                      class="account-settings__how-it-works"
                      @click="toggleDescription(key)"
                    >
                      <span>{{ t('howItWorks') }}</span>
                      <img
                        :src="require('@/assets/img/account/settings/smallChevron.svg')"
                        alt=""
                        class="account-settings__how-it-works-chevron"
                        :class="{ 'account-settings__how-it-works-chevron--open': openDescriptions[key] }"
                      />
                    </button>
                  </div>
                  <transition name="account-settings-expand">
                    <div v-if="openDescriptions[key]" class="account-settings__field-description account-settings__field-description--expanded">
                      {{ t('promptDescription') }}
                    </div>
                  </transition>
                  <div class="account-settings__field-input account-settings__field-input--block">
                    <div class="prompt-section">
                      <div class="prompt-section__toggle">
                        <VBtn
                          text
                          small
                          color="primary"
                          class="prompt-section__toggle-btn"
                          @click="isImmutablePromptPartOpen = !isImmutablePromptPartOpen"
                        >
                          {{ isImmutablePromptPartOpen ? t('promptHidePrefix') : t('promptShowPrefix') }}
                        </VBtn>
                      </div>
                      <VExpandTransition>
                        <div v-show="isImmutablePromptPartOpen" class="prompt-section__readonly">
                          <pre class="prompt-preview">{{ settings.immutablePromptPart || '' }}</pre>
                        </div>
                      </VExpandTransition>

                      <Tooltip
                        v-if="isDisabled"
                        :position="'bottom'"
                        :position-mob="'left'"
                        :max-width="400"
                        class="account-settings__field-input-tooltip"
                      >
                        <template #trigger>
                          <div class="account-settings__field-input-wrapper">
                            <VTextarea
                              v-model="settings.mutablePromptPart"
                              :placeholder="t('promptMutablePlaceholder')"
                              outlined
                              hide-details
                              rows="1"
                              auto-grow
                              :disabled="true"
                              @input="handleSettingChange('mutablePromptPart', $event)"
                            />
                          </div>
                        </template>
                        <template #content>
                          <div class="tooltip__content-text-wrapper">
                            <div class="tooltip__content-text">
                              {{ t('upgradeTooltip') }}
                            </div>
                            <VBtn
                              color="success"
                              small
                              @click="redirectToUpgradePlan"
                            >
                              {{ t('toUpgradePlan') }}
                            </VBtn>
                          </div>
                        </template>
                      </Tooltip>
                      <div v-else class="account-settings__field-input-wrapper">
                        <VTextarea
                          v-model="settings.mutablePromptPart"
                          :placeholder="t('promptMutablePlaceholder')"
                          outlined
                          hide-details
                          rows="1"
                          auto-grow
                          class="prompt-section__textarea"
                          @input="handleSettingChange('mutablePromptPart', $event)"
                        />
                      </div>

                      <div class="prompt-section__toggle">
                        <VBtn
                          text
                          small
                          color="primary"
                          class="prompt-section__toggle-btn"
                          @click="isImmutablePromptPostfixPartOpen = !isImmutablePromptPostfixPartOpen"
                        >
                          {{ isImmutablePromptPostfixPartOpen ? t('promptHidePostfix') : t('promptShowPostfix') }}
                        </VBtn>
                      </div>
                      <VExpandTransition>
                        <div v-show="isImmutablePromptPostfixPartOpen" class="prompt-section__readonly">
                          <pre class="prompt-preview">{{ settings.immutablePromptPostfixPart || '' }}</pre>
                        </div>
                      </VExpandTransition>
                    </div>
                  </div>
                </template>

                <template v-else-if="isBooleanField(key)">
                  <div class="account-settings__field-row-header account-settings__field-row-header--has-switch">
                    <Tooltip
                      v-if="isDisabled"
                      :position="'bottom'"
                      :position-mob="'left'"
                      :max-width="400"
                    >
                      <template #trigger>
                        <VSwitch
                          v-model="settings[key]"
                          hide-details
                          inset
                          :disabled="true"
                          @change="handleSettingChange(key, $event)"
                        />
                      </template>
                      <template #content>
                        <div class="tooltip__content-text-wrapper">
                          <div class="tooltip__content-text">
                            {{ t('upgradeTooltip') }}
                          </div>
                          <VBtn
                            color="success"
                            small
                            @click="redirectToUpgradePlan"
                          >
                            {{ t('toUpgradePlan') }}
                          </VBtn>
                        </div>
                      </template>
                    </Tooltip>
                    <VSwitch
                      v-else
                      v-model="settings[key]"
                      hide-details
                      inset
                      :disabled="false"
                      @change="handleSettingChange(key, $event)"
                    />

                    <div class="account-settings__field-label-wrapper">
                      <div class="account-settings__field-label">
                        {{ getSettingLabel(key) }}
                      </div>
                    </div>
                    <button
                      v-if="hasBooleanDescription(key)"
                      class="account-settings__how-it-works"
                      @click="toggleDescription(key)"
                    >
                      <span>{{ t('howItWorks') }}</span>
                      <img
                        :src="require('@/assets/img/account/settings/smallChevron.svg')"
                        alt=""
                        class="account-settings__how-it-works-chevron"
                        :class="{ 'account-settings__how-it-works-chevron--open': openDescriptions[key] }"
                      />
                    </button>
                  </div>
                  <transition name="account-settings-expand">
                    <div v-if="openDescriptions[key]" class="account-settings__field-description account-settings__field-description--expanded">
                      <p
                        v-for="paragraph in getSettingDescriptionParts(key).paragraphs"
                        :key="paragraph"
                      >
                        {{ paragraph }}
                      </p>
                      <ul v-if="getSettingDescriptionParts(key).items.length">
                        <li
                          v-for="item in getSettingDescriptionParts(key).items"
                          :key="item"
                        >
                          {{ item }}
                        </li>
                      </ul>
                    </div>
                  </transition>
                </template>

                <template v-else>
                  <div class="account-settings__field-label-wrapper">
                    <div class="account-settings__field-row-header">
                      <div class="account-settings__field-label">
                        {{ getSettingLabel(key) }}
                      </div>
                      <button
                        v-if="hasNonBooleanDescription(key)"
                        class="account-settings__how-it-works"
                        @click="toggleDescription(key)"
                      >
                        <span>{{ t('howItWorks') }}</span>
                        <img
                          :src="require('@/assets/img/account/settings/smallChevron.svg')"
                          alt=""
                          class="account-settings__how-it-works-chevron"
                          :class="{ 'account-settings__how-it-works-chevron--open': openDescriptions[key] }"
                        />
                      </button>
                    </div>
                    <transition name="account-settings-expand">
                      <div v-if="openDescriptions[key]" class="account-settings__field-description account-settings__field-description--expanded">
                        <p
                          v-for="paragraph in getSettingDescriptionParts(key).paragraphs"
                          :key="paragraph"
                        >
                          {{ paragraph }}
                        </p>
                        <ul v-if="getSettingDescriptionParts(key).items.length">
                          <li
                            v-for="item in getSettingDescriptionParts(key).items"
                            :key="item"
                          >
                            {{ item }}
                          </li>
                        </ul>
                      </div>
                    </transition>
                  </div>
                  <div class="account-settings__field-input">
                    <Tooltip
                      v-if="isDisabled"
                      :position="'bottom'"
                      :position-mob="'left'"
                      :max-width="400"
                    >
                      <template #trigger>
                        <div class="account-settings__field-input-wrapper">
                          <VSelect
                            v-if="key === 'autoJobProcessorInviteType'"
                            v-model="settings[key]"
                            :items="inviteTypeOptions"
                            item-text="text"
                            item-value="value"
                            outlined
                            dense
                            hide-details
                            :disabled="true"
                            @change="handleSettingChange(key, $event)"
                          />
                          <VTextarea
                            v-else-if="isMessageField(key)"
                            v-model="settings[key]"
                            :placeholder="getPlaceholder(key)"
                            outlined
                            hide-details
                            rows="1"
                            auto-grow
                            :disabled="true"
                            @input="handleSettingChange(key, $event)"
                          />
                        </div>
                      </template>
                      <template #content>
                        <div class="tooltip__content-text-wrapper">
                          <div class="tooltip__content-text">
                            {{ t('upgradeTooltip') }}
                          </div>
                          <VBtn
                            color="success"
                            small
                            @click="redirectToUpgradePlan"
                          >
                            {{ t('toUpgradePlan') }}
                          </VBtn>
                        </div>
                      </template>
                    </Tooltip>
                    <template v-else>
                      <VSelect
                        v-if="key === 'autoJobProcessorInviteType'"
                        v-model="settings[key]"
                        :items="inviteTypeOptions"
                        item-text="text"
                        item-value="value"
                        outlined
                        dense
                        hide-details
                        @change="handleSettingChange(key, $event)"
                      />
                      <VTextarea
                        v-else-if="isMessageField(key)"
                        v-model="settings[key]"
                        :placeholder="getPlaceholder(key)"
                        outlined
                        hide-details
                        rows="1"
                        auto-grow
                        @input="handleSettingChange(key, $event)"
                      />
                    </template>
                  </div>
                </template>
              </div>
            </div>
          </template>
    </SettingsCard>
  </VCol>
</template>

<script>
import {
  ref, computed, onMounted, onBeforeUnmount,
} from '@vue/composition-api';
import useAccount from '@/compositions/accounting/useAccount';
import isEqual from 'lodash.isequal';
import Tooltip from '@/components/common/Tooltip.vue';
import Preloader from '@/components/Preloader';
import SettingsCard from '@/components/Settings/SettingsCard.vue';
import { paymentTabs, settingsTabs } from '@/enums';
import { defaultPromptParts, promptKeys } from '@/helpers/aiProcessorPromptParts';

export default {
  name: 'AiProcessorSettingsCard',
  components: {
    SettingsCard,
    Tooltip,
    Preloader,
  },
  props: {
    t: {
      type: Function,
      required: true,
    },
    fetchSettings: {
      type: Function,
      required: true,
    },
    saveSettings: {
      type: Function,
      required: true,
    },
  },
  setup(props, { root }) {
    const { account, fetchIfNull } = useAccount();

    const isSaving = ref(false);
    const isLoading = ref(false);
    const originalSettings = ref({});
    const settings = ref({});
    let autosaveTimer = null;
    let isSaveQueued = false;
    const autosaveDelay = 700;

    const isImmutablePromptPartOpen = ref(false);
    const isImmutablePromptPostfixPartOpen = ref(false);
    const openDescriptions = ref({});

    const inviteTypeOptions = computed(() => [
      { text: props.t('inviteTypeAll'), value: 'all' },
      { text: props.t('inviteTypeSuitable'), value: 'suitable' },
    ]);

    const isDisabled = computed(() => {
      return account.value?.hhAiProcessor === false;
    });

    const booleanFields = [
      'autoJobProcessor',
      'autoJobProcessorHhInviteEnabled',
      'autoJobProcessorSmsInviteEnabled',
      'autoJobProcessorCallInviteEnabled',
      'autoJobProcessorEmailInviteEnabled',
      'autoJobProcessorRejectUnsuitableHh',
      'autoJobProcessorHhInviteAttachLink',
    ];

    const messageFields = [
      'autoJobProcessorHhInviteMessage',
      'autoJobProcessorHhRejectMessage',
    ];

    const noBorderFields = [
      'autoJobProcessorInviteType',
      'autoJobProcessorEmailInviteEnabled',
      'autoJobProcessorSmsInviteEnabled',
      'autoJobProcessorCallInviteEnabled',
      'autoJobProcessorHhInviteAttachLink',
      'autoJobProcessorRejectUnsuitableHh',
    ];

    const isBooleanField = (key) => booleanFields.includes(key);
    const isNoBorderField = (key) => noBorderFields.includes(key);
    const isMessageField = (key) => messageFields.includes(key);

    const fieldOrder = [
      'autoJobProcessor',
      'autoJobProcessorHhInviteEnabled',
      'prompt',
      'autoJobProcessorInviteType',
      'autoJobProcessorEmailInviteEnabled',
      'autoJobProcessorSmsInviteEnabled',
      'autoJobProcessorCallInviteEnabled',
      'autoJobProcessorHhInviteMessage',
      'autoJobProcessorHhInviteAttachLink',
      'autoJobProcessorRejectUnsuitableHh',
      'autoJobProcessorHhRejectMessage',
    ];

    const filteredSettings = computed(() => {
      if (!settings.value || typeof settings.value !== 'object') {
        return [];
      }
      const excluded = new Set(['id', 'vacancyId', 'companyId', 'createdAt', 'updatedAt', ...promptKeys]);
      return fieldOrder.filter((k) => {
        if (k === 'prompt') return true;
        return !excluded.has(k) && k in settings.value;
      });
    });

    const normalizeSettings = (data) => {
      const normalized = { ...data };

      if (normalized.autoJobProcessorHhInviteMessage === null) {
        normalized.autoJobProcessorHhInviteMessage = 'Приглашаем вас пройти собеседование.';
      }

      if (normalized.autoJobProcessorHhRejectMessage === null) {
        normalized.autoJobProcessorHhRejectMessage = 'К сожалению, сейчас мы не можем пригласить вас на эту вакансию. Спасибо что проявили интерес!';
      }

      if (!normalized.immutablePromptPart) {
        normalized.immutablePromptPart = defaultPromptParts.immutablePromptPart;
      }

      if (!normalized.immutablePromptPostfixPart) {
        normalized.immutablePromptPostfixPart = defaultPromptParts.immutablePromptPostfixPart;
      }

      booleanFields.forEach((field) => {
        if (normalized[field] === null || normalized[field] === undefined) {
          normalized[field] = false;
        }
      });

      if (normalized.autoJobProcessorInviteType === 'null' || normalized.autoJobProcessorInviteType === null || normalized.autoJobProcessorInviteType === undefined) {
        normalized.autoJobProcessorInviteType = 'all';
      }

      if (normalized.mutablePromptPart === null || normalized.mutablePromptPart === undefined) {
        normalized.mutablePromptPart = defaultPromptParts.mutablePromptPart;
      }

      return normalized;
    };

    const load = async () => {
      try {
        isLoading.value = true;
        const result = await props.fetchSettings();
        const data = result?.data || {};
        const normalizedData = normalizeSettings(data);
        originalSettings.value = { ...normalizedData };
        settings.value = { ...normalizedData };
      } catch (error) {
        root.$toast.error(error?.message || props.t('errorLoading') || 'Ошибка при загрузке настроек');
      } finally {
        isLoading.value = false;
      }
    };

    const clearAutosaveTimer = () => {
      if (autosaveTimer) {
        clearTimeout(autosaveTimer);
        autosaveTimer = null;
      }
    };

    const save = async () => {
      if (isDisabled.value || isEqual(originalSettings.value, settings.value)) {
        return;
      }

      if (isSaving.value) {
        isSaveQueued = true;
        return;
      }

      const settingsSnapshot = { ...settings.value };

      try {
        isSaving.value = true;
        await props.saveSettings(settingsSnapshot);
        originalSettings.value = { ...settingsSnapshot };
      } catch (error) {
        root.$toast.error(error?.message || props.t('errorSaving') || 'Ошибка при сохранении');
      } finally {
        isSaving.value = false;
        if (isSaveQueued) {
          isSaveQueued = false;
          await save();
        }
      }
    };

    const scheduleAutosave = () => {
      clearAutosaveTimer();
      autosaveTimer = setTimeout(() => {
        autosaveTimer = null;
        save();
      }, autosaveDelay);
    };

    const handleSettingChange = (key, value) => {
      settings.value[key] = value;

      if (isMessageField(key) || key === 'mutablePromptPart') {
        scheduleAutosave();
        return;
      }

      clearAutosaveTimer();
      save();
    };

    const getSettingLabel = (key) => {
      const translationKey = `settings.${key}`;
      const translation = props.t(translationKey);
      if (translation === translationKey) {
        return key;
      }
      return translation;
    };

    const getPlaceholder = (key) => {
      if (key === 'autoJobProcessorHhInviteMessage') return props.t('placeholderInviteMessage');
      if (key === 'autoJobProcessorHhRejectMessage') return props.t('placeholderRejectMessage');
      return '';
    };

    const getSettingDescription = (key) => {
      const translationKey = `settingsDescription.${key}`;
      const translation = props.t(translationKey);
      if (translation === translationKey) {
        return '';
      }
      return translation;
    };

    const getSettingDescriptionParts = (key) => {
      const description = getSettingDescription(key);
      if (!description) {
        return { paragraphs: [], items: [] };
      }

      if (typeof description === 'string') {
        return { paragraphs: [description], items: [] };
      }

      const paragraphs = [];
      const items = [];
      Object.keys(description).forEach((descriptionKey) => {
        if (descriptionKey.startsWith('li')) {
          items.push(description[descriptionKey]);
        } else {
          paragraphs.push(description[descriptionKey]);
        }
      });

      return { paragraphs, items };
    };

    const hasBooleanDescription = (key) => isBooleanField(key) && Boolean(getSettingDescription(key));
    const hasNonBooleanDescription = (key) => !isBooleanField(key) && Boolean(getSettingDescription(key));

    const toggleDescription = (key) => {
      openDescriptions.value = {
        ...openDescriptions.value,
        [key]: !openDescriptions.value[key],
      };
    };

    const redirectToUpgradePlan = () => {
      root.$router.replace({
        name: 'settings',
        params: {
          type: settingsTabs.PAYMENT,
          addType: paymentTabs.UPGRADE,
        },
      });
    };

    onMounted(async () => {
      await fetchIfNull();
      await load();
    });

    onBeforeUnmount(() => {
      clearAutosaveTimer();
    });

    return {
      isLoading,
      isDisabled,
      isImmutablePromptPartOpen,
      isImmutablePromptPostfixPartOpen,
      openDescriptions,
      settings,
      filteredSettings,
      inviteTypeOptions,
      isBooleanField,
      isNoBorderField,
      isMessageField,
      getSettingLabel,
      getPlaceholder,
      getSettingDescription,
      getSettingDescriptionParts,
      hasBooleanDescription,
      hasNonBooleanDescription,
      toggleDescription,
      handleSettingChange,
      redirectToUpgradePlan,
    };
  },
};
</script>

<style lang="scss" scoped>
.account-settings {
  &__fields {
    margin-top: 20px;
    @media screen and (max-width: 768px) {
      margin-top: 16px;
    }
  }
  &__field-row {
    display: grid;
    grid-template-columns: 460px 1fr;
    gap: 20px;
    align-items: center;
    padding: 15px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    &:last-child {
      border-bottom: none;
    }
    @media screen and (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 10px;
    }
    &--switch {
      display: block;
      align-items: unset;
    }
    &--no-border {
      border-bottom: none;
    }
    &-header {
      display: flex;
      align-items: center;
      width: 100%;

      ::v-deep .v-input--selection-controls {
        margin-top: 0;
        padding-top: 0;
        flex-shrink: 0;
      }

      .account-settings__field-label {
        padding-top: 0;
        flex: 1;
      }
    }
  }
  &__how-it-works {
    display: inline-flex;
    align-items: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;
    margin-left: auto;
    font-size: 12px;
    color: rgba(112, 112, 112, 1);
    font-weight: 400;
    line-height: 120%;
    white-space: nowrap;

    span {
      margin-right: 4px;
    }

    &-chevron {
      transition: transform 0.25s ease;
      flex-shrink: 0;
      display: block;

      &--open {
        transform: rotate(180deg);
      }
    }
  }
  &__field-label-wrapper {
    display: flex;
    flex-direction: column;
  }
  &__field-label {
    font-size: 16px;
    color: #212121;
    padding-top: 8px;
    @media screen and (max-width: 768px) {
      font-size: 14px;
      padding-top: 0;
    }
  }
  &__field-description {
    font-size: 12px;
    line-height: 140%;
    font-weight: 400;
    color: #707070;
    margin-top: 4px;
    @media screen and (max-width: 768px) {
      font-size: 11px;
    }

    &--expanded {
      margin-top: 14px;
    }

    p {
      margin: 0;

      & + p {
        margin-top: 8px;
      }
    }

    ul {
      margin: 8px 0 0;
      padding-left: 16px;

      li + li {
        margin-top: 2px;
      }
    }
  }

  &__field-input {
    width: 100%;
    max-width: 100%;
    margin-top: 12px;
    @media screen and (max-width: 768px) {
      width: 100%;
      max-width: 100%;
    }

    &--block {
      width: 100%;
      margin-top: 12px;

      ::v-deep .v-text-field--outlined .v-input__slot {
        min-height: 43px;
      }

      ::v-deep .v-select.v-text-field--outlined.v-input--dense .v-input__slot {
        min-height: 43px;
        height: 43px;
      }

      ::v-deep .v-textarea .v-input__slot {
        min-height: 43px;
      }
    }

    &-tooltip {
      width: 100%;
      display: block;
    }
    &-wrapper {
      width: 100%;
      display: block;
    }
  }

  &__field-input--block {
    margin-top: 12px;
  }

}

.prompt-section {
  &__toggle {
    margin-top: 12px;

    &:first-child {
      margin-top: 0;
    }
  }

  &__textarea {
    ::v-deep .v-input__slot {
      background: #fafafa !important;
      min-height: 43px !important;
      padding: 0 12px !important;
    }

    ::v-deep textarea {
      font-size: 12px;
      line-height: 140%;
      color: #212121;
      margin-top: 8px;
      margin-bottom: 8px;
    }
  }

  &__readonly {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    padding: 12px;
    background: #fafafa;
    margin-top: 8px;
  }

  .prompt-preview {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: inherit;
    font-size: 12px;
    line-height: 140%;
    color: #212121;
  }
}

.account-settings-expand-enter-active,
.account-settings-expand-leave-active {
  transition: opacity 0.25s ease, max-height 0.3s ease, margin-top 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.account-settings-expand-enter,
.account-settings-expand-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0 !important;
}
</style>

